import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { ContractChangeItemDataSortInput } from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';

import type { IGetRowsParams } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getAgreementsRegistry } from '../AgreementsQueries';

const splitDateString = (dateStr: string) => dateStr.split('T')[0];
const createVariablesArr = (
    values: string[],
    field: 'createdAt' | 'signingDate',
    prevState: {
        and: {
            or: { and: Record<'createdAt' | 'signingDate', any>[] }[];
        };
    },
) => {
    const prevStateWithoutCreatedAt = prevState?.and?.or?.filter((obj) => !obj.and.some((item) => item[field])) || [];

    return {
        or: [
            ...prevStateWithoutCreatedAt,
            ...values.reduce((acc: OrDateType, date) => {
                const nextDayDate = new Date(date);

                nextDayDate.setDate(nextDayDate.getDate() + 1);
                if (date === null) {
                    acc.push({
                        and: [
                            {
                                [field]: {
                                    eq: date,
                                },
                            },
                        ],
                    });
                } else {
                    acc.push({
                        and: [
                            {
                                [field]: {
                                    gte: splitDateString(date),
                                },
                            },
                            {
                                [field]: {
                                    lte: splitDateString(nextDayDate.toISOString()),
                                },
                            },
                        ],
                    });
                }

                return acc;
            }, []),
        ],
    };
};

type OrDateType = Record<string, Record<string, Record<string, string | null>>[]>[];
const getWhereVariablesFromFilter = (fieldName: string, valuesArr: string[], prevState: any) => {
    if (!valuesArr.length && prevState.and[fieldName]) {
        return { [fieldName]: undefined };
    }

    if (fieldName === 'createdAt') {
        return createVariablesArr(valuesArr, fieldName, prevState);
    }

    if (fieldName === 'signingDate') {
        return createVariablesArr(valuesArr, fieldName, prevState);
    }

    return fieldName === 'status'
        ? { [fieldName]: { in: valuesArr } }
        : {
              [fieldName]: {
                  or: valuesArr.map((item: string) => ({
                      eq: item,
                  })),
              },
          };
};

export const useGetAgreementRegistry = ({
    contractId,
    contractHasContractChanges,
}: {
    contractId: number;
    contractHasContractChanges: boolean;
}) => {
    const gridApiRef = useRef<AgGridReact<any> | null>(null);

    const [queryWhere, setQueryWhere] = useState<Record<string, any> | undefined>(undefined);
    const [filtersWhere, setFiltersWhere] = useState<Record<string, any> | undefined>(undefined);
    const [debounceValue, setDebounceValue] = useState('');
    const dispatch = useDispatch();

    const variables = useMemo(
        () =>
            contractId
                ? {
                      contractId,
                      skip: 0,
                      take: 50,
                      where: filtersWhere || queryWhere,
                  }
                : undefined,
        [contractId, filtersWhere, queryWhere],
    );

    const handleSearch = useCallback(({ value }: { value: string }) => {
        setDebounceValue(value);
        setFiltersWhere(undefined);
        setQueryWhere(
            value
                ? {
                      or: [
                          {
                              subject: {
                                  containsi: value,
                              },
                          },
                          {
                              internalNumber: {
                                  containsi: value,
                              },
                          },
                      ],
                  }
                : undefined,
        );
    }, []);

    const changeFilterHandler = useCallback(({ fieldName, checkedValues }) => {
        setFiltersWhere((prevValues) => ({
            and: {
                ...prevValues?.and,
                ...getWhereVariablesFromFilter(fieldName, checkedValues, prevValues),
            },
        }));
    }, []);

    const [agreementData, agreementStatus, agreementError, agreementRequest] = useRequest({
        requestId: 'agreementRegistry',
        query: getAgreementsRegistry,
        withEffect: false,
    });

    const getOrder = useCallback<(params: IGetRowsParams) => ContractChangeItemDataSortInput>(
        (params: IGetRowsParams) => {
            const colId = params?.sortModel?.[0]?.colId;
            const sort = params?.sortModel?.[0]?.sort;

            return colId && sort ? { [colId]: sort.toUpperCase() } : { createdAt: SortEnumType.Desc };
        },
        [],
    );

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId: 'agreementRegistry',
        requestFn: agreementRequest,
        requestVariables: variables,
        customSortFn: getOrder,
    });

    const refreshAgreementData = useCallback(() => {
        dispatch(agreementRequest(variables));
    }, [agreementRequest, dispatch, variables]);

    useEffect(() => {
        if (!contractHasContractChanges) {
            getRowParams?.current?.successCallback([], 0);

            return;
        }

        if (agreementData?.uerContractChanges?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                agreementData?.uerContractChanges?.items,
                agreementData?.uerContractChanges?.totalCount,
            );
        }
    }, [
        agreementData?.uerContractChanges?.items,
        agreementData?.uerContractChanges?.totalCount,
        contractHasContractChanges,
        getRowParams,
    ]);

    const isRegistryLoading = useMemo(() => agreementStatus === RequestStatus.FETCHING, [agreementStatus]);

    const refreshData = useCallback(() => {
        if (gridApiRef.current) {
            gridApiRef.current.api.refreshInfiniteCache();
        }
    }, []);

    const hasActiveFilters = useMemo(() => filtersWhere !== undefined, [filtersWhere]);
    const isLastItem = useMemo(
        () => agreementData?.uerContractChanges?.totalCount === 1,
        [agreementData?.uerContractChanges?.totalCount],
    );

    return {
        agreementData: agreementData?.uerContractChanges?.items || [],
        agreementStatus,
        isRegistryLoading,
        agreementError,
        agreementRequest,
        handleSearch,
        refreshData,
        changeFilterHandler,
        hasActiveFilters,
        dataSource,
        isLastItem,
        gridApiRef,
        queryWhere,
        key: debounceValue,
        refreshAgreementData,
    };
};
